import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { X, KeyRound, ArrowRight, CheckCircle2, UserCheck, Upload, MapPin, Mail, User as UserIcon } from 'lucide-react';
import { useToast } from '../ui/Toast';

export const AuthModal: React.FC = () => {
  const {
    authModalOpen,
    setAuthModalOpen,
    isAuthenticated,
    isProfileCompleted,
    sendOtp,
    verifyOtp,
    completeProfile,
  } = useApp();
  const { showToast } = useToast();

  // Step state: 1 = Mobile Input, 2 = OTP Verification, 3 = Profile Setup
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [profilePicture, setProfilePicture] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Sync step with authentication state on modal open/change
  useEffect(() => {
    if (isAuthenticated && !isProfileCompleted) {
      setStep(3);
    } else if (!isAuthenticated) {
      setStep(1);
    }
  }, [authModalOpen, isAuthenticated, isProfileCompleted]);

  if (!authModalOpen) return null;

  // Step 1: Request OTP
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!mobile.trim() || mobile.trim().length < 10) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }

    setSubmitting(true);
    const result = await sendOtp(mobile.trim());
    setSubmitting(false);

    if (result.success) {
      showToast('OTP sent successfully to ' + mobile.trim(), 'success');
      setStep(2);
    } else {
      setError(result.error || 'Failed to send OTP.');
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!otp.trim() || otp.trim().length !== 6) {
      setError('Please enter a 6-digit OTP code.');
      return;
    }

    setSubmitting(true);
    const result = await verifyOtp(mobile.trim(), otp.trim());
    setSubmitting(false);

    if (result.success) {
      if (result.is_profile_completed === 1) {
        showToast('Authenticated successfully!', 'success');
        setAuthModalOpen(false);
      } else {
        showToast('OTP verified! Please complete your profile.', 'info');
        setStep(3);
      }
    } else {
      setError(result.error || 'Invalid OTP code.');
    }
  };

  // Step 3: Profile Setup
  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('Full Name is required.');
      return;
    }

    setSubmitting(true);
    const result = await completeProfile(name.trim(), email.trim(), city.trim(), profilePicture.trim());
    setSubmitting(false);

    if (result.success) {
      showToast('Profile completed! Welcome to REVise.', 'success');
      setAuthModalOpen(false);
    } else {
      setError(result.error || 'Failed to update profile.');
    }
  };

  // File uploader calling /api/upload_profile_picture.php
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setError('Image file size must be less than 2MB.');
      return;
    }

    const formData = new FormData();
    formData.append('profile_picture', file);

    setSubmitting(true);
    setError('');

    try {
      const res = await fetch('/api/upload_profile_picture.php', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success && data?.url) {
        setProfilePicture(data.url);
        showToast('Profile picture uploaded successfully!', 'success');
      } else {
        setError(data?.error || 'Failed to upload profile picture.');
      }
    } catch {
      setError('Failed to upload image. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#111111]/40 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-7 shadow-2xl border border-[#E5E9E9] animate-in fade-in zoom-in-95 duration-150">
        {/* Header Bar */}
        <div className="flex items-center justify-between pb-3 border-b border-[#E5E9E9] mb-4">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#1b8e88]" />
            <h3 className="text-base font-bold text-[#111111]">
              {step === 1 && 'Welcome to REVise'}
              {step === 2 && 'Verify Mobile Number'}
              {step === 3 && 'Complete Your Profile'}
            </h3>
          </div>
          {/* Prevent closing if authenticated but profile incomplete */}
          {(!isAuthenticated || isProfileCompleted) && (
            <button
              onClick={() => setAuthModalOpen(false)}
              className="text-[#5F6368] hover:text-[#111111] p-1 rounded-lg cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className={`h-1.5 flex-1 rounded-full ${step >= 1 ? 'bg-[#56E8E1]' : 'bg-[#E5E9E9]'}`} />
          <div className={`h-1.5 flex-1 rounded-full ${step >= 2 ? 'bg-[#56E8E1]' : 'bg-[#E5E9E9]'}`} />
          <div className={`h-1.5 flex-1 rounded-full ${step >= 3 ? 'bg-[#56E8E1]' : 'bg-[#E5E9E9]'}`} />
        </div>

        {error && (
          <div className="mb-4 p-2.5 rounded-xl bg-[#FDEDEF] border border-[#D94A5C]/30 text-xs text-[#D94A5C] font-medium">
            {error}
          </div>
        )}

        {/* STEP 1: Enter Mobile */}
        {step === 1 && (
          <form onSubmit={handleSendOtp} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#111111] mb-1">
                Mobile Number *
              </label>
              <input
                type="tel"
                required
                maxLength={15}
                placeholder="Enter 10-digit mobile number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-[#E5E9E9] text-sm focus:outline-none focus:border-[#56E8E1] bg-white font-medium"
              />
              <p className="text-[10px] text-[#5F6368] mt-1">
                We will send an OTP verification code to this mobile number.
              </p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-xs cursor-pointer disabled:opacity-50"
            >
              {submitting ? 'Sending OTP...' : 'Continue'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}

        {/* STEP 2: Enter OTP */}
        {step === 2 && (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#111111] mb-1">
                Enter 6-Digit OTP Code *
              </label>
              <input
                type="text"
                required
                maxLength={6}
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-[#E5E9E9] text-center tracking-widest text-lg font-bold focus:outline-none focus:border-[#56E8E1] bg-white"
              />
              <div className="flex justify-between items-center mt-2">
                <span className="text-[10px] text-[#5F6368]">
                  Sent to: <strong>{mobile}</strong>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setError('');
                  }}
                  className="text-[10px] font-bold text-[#1b8e88] hover:underline cursor-pointer"
                >
                  Change Mobile
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-xs cursor-pointer disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" />
              {submitting ? 'Verifying OTP...' : 'Verify & Continue'}
            </button>
          </form>
        )}

        {/* STEP 3: One-Time Profile Setup */}
        {step === 3 && (
          <form onSubmit={handleProfileSubmit} className="space-y-3">
            <p className="text-xs text-[#5F6368] mb-2">
              Please set up your profile details to complete registration.
            </p>

            <div>
              <label className="block text-xs font-bold text-[#111111] mb-1 flex items-center gap-1">
                <UserIcon className="w-3.5 h-3.5 text-[#1b8e88]" /> Full Name *
              </label>
              <input
                type="text"
                required
                placeholder="e.g. Rahul Sharma"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3.5 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#111111] mb-1 flex items-center gap-1">
                <Mail className="w-3.5 h-3.5 text-[#1b8e88]" /> Email Address (Optional)
              </label>
              <input
                type="email"
                placeholder="e.g. rahul@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3.5 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#111111] mb-1 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5 text-[#1b8e88]" /> City / Location (Optional)
              </label>
              <input
                type="text"
                placeholder="e.g. Delhi, Mumbai"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full px-3.5 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#111111] mb-1 flex items-center gap-1">
                <Upload className="w-3.5 h-3.5 text-[#1b8e88]" /> Profile Picture (Optional)
              </label>
              <div className="flex items-center gap-3">
                {profilePicture ? (
                  <img
                    src={profilePicture}
                    alt="Preview"
                    className="w-10 h-10 rounded-full object-cover border border-[#56E8E1]"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#F0F4F4] border border-[#E5E9E9] flex items-center justify-center text-xs font-bold text-[#5F6368]">
                    Pic
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="text-xs text-[#5F6368] file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-[#F0F4F4] file:text-[#111111] hover:file:bg-[#E5E9E9] cursor-pointer"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-xs cursor-pointer disabled:opacity-50 mt-3"
            >
              <UserCheck className="w-4 h-4" />
              {submitting ? 'Completing Profile...' : 'Continue to REVise'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
